import { formMeta } from '../data/forms.js';
import type { Form } from '../fixtures/domain.js';
import { Button } from '../components/Button.js';
import { EmptyState } from '../components/EmptyState.js';
import { Icon } from '../components/Icon.js';
import { LoadingState } from '../components/LoadingState.js';
import styles from './FormsPanel.module.css';

/**
 * The form library, and only the library — templates live in the context
 * column and the gallery, never as rows in here: a shared form sitting in
 * your list reads as one you forgot writing.
 *
 * Rows carry what a form is, never how it has done: answers live on the
 * messages that carry them, so a count here would mean walking every channel
 * in the account.
 */
export function FormsPanel({
  forms,
  openFormId,
  loading,
  error = false,
  onRetry,
  onOpen,
  onNew,
}: {
  forms: Form[];
  openFormId: string | null;
  loading: boolean;
  /** The library read failed; `onRetry` asks again. */
  error?: boolean;
  onRetry?: () => void;
  onOpen: (formId: string) => void;
  /** Start a form. The header's "+" and the empty state's button are the same
   *  press: what opens is the gallery of starting points, blank card first. */
  onNew: () => void;
}) {
  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <h1 className={styles.title}>Forms</h1>
        <button className={styles.newForm} title="New form" aria-label="New form" onClick={onNew}>
          <Icon name="plus" size={20} />
        </button>
      </div>

      <div className={styles.list}>
        {error ? (
          <div className={styles.empty}>
            <EmptyState
              icon={<Icon name="listChecks" size={32} />}
              title="Couldn't load your forms"
              description="Something went wrong while loading the library."
            />
            {onRetry && <Button onClick={onRetry}>Try again</Button>}
          </div>
        ) : loading ? (
          <LoadingState label="Fetching your forms" />
        ) : forms.length === 0 ? (
          <EmptyState
            icon={<Icon name="listChecks" size={32} />}
            title="No forms yet"
            description="Create a form for jobseekers to answer. Send it from any chat, or attach it to your auto reply."
            action={{ label: 'Create a form', onClick: onNew }}
          />
        ) : (
          forms.map((form) => (
            <button
              key={form.id}
              type="button"
              className={form.id === openFormId ? styles.rowActive : styles.row}
              aria-current={form.id === openFormId}
              onClick={() => onOpen(form.id)}
            >
              <span className={styles.tile}>
                <Icon name="listChecks" size={16} />
              </span>
              <span className={styles.rowText}>
                <span className={styles.rowName}>{form.name}</span>
                <span className={styles.rowMeta}>{formMeta(form)}</span>
              </span>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
